import {
  Button,
  Flex,
  Heading,
  IconButton,
  Skeleton,
  Spacer,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { Form, Formik, FormikHelpers } from "formik";
import { InputControl } from "formik-chakra-ui";
import React from "react";
import { RiExternalLinkLine, RiInformationLine } from "react-icons/ri";
import { Link as RouterLink } from "react-router-dom";
import { GenericError } from "src/components/Feedback/GenericError";
import { FormikSwitchableContent } from "src/components/Inputs/FormikSwitchableContent";
import { AdminFormButtons } from "src/components/Layout/AdminFormButtons";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import { LearnMore } from "src/components/Links/LearnMore";
import { NavigationBlocker } from "src/components/NavigationBlocker";
import * as FSQ from "src/generated/hooks/form-service/queries";
import {
  CapacityConfig,
  updateCapacitiesConfigByEnrollmentPeriod,
} from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { useOrganization } from "src/hooks/useOrganization";
import { validateWithZod } from "src/services/formValidations";
import { isNonEmptyString } from "src/services/predicates";
import * as Url from "src/services/url";
import * as RD from "src/types/remoteData";
import { z } from "zod";
import { Capacities } from "../../capacities/Capacities";

type Props = {
  enrollmentPeriodId: string;
};
export const CapacityTab: React.FC<Props> = ({ enrollmentPeriodId }) => {
  const organizationRD = useOrganization();
  const query = FSQ.useGetCapacitiesConfigByEnrollmentPeriod({
    path: {
      enrollmentPeriodId,
    },
  });

  const remoteData = RD.fromRestResult(query).mapError(RD.errorResponseToError);
  const queryClient = useQueryClient();
  const avelaToast = useAvelaToast();
  const { confirm, confirmationDialog } = useConfirmationDialog({
    header: "Update capacities?",
    body: "Changes to additional seats modify available seats, which may impact making offers and filters by capacities. This can’t be undone or recovered.",
    confirmButton: {
      label: "Yes, update",
    },
    cancelButton: {
      label: "No, cancel",
    },
  });

  return (
    <>
      <RemoteDataView
        remoteData={RD.toTuple(remoteData, organizationRD)}
        error={(error) => <GenericError error={error ?? undefined} />}
        loading={<Loading />}
        reloading={null}
      >
        {([{ data }, organization]) => (
          <Formik<FormikType>
            initialValues={{
              switch:
                data.additionalSeatsTaken1.enabled ||
                data.additionalSeatsTaken2.enabled
                  ? "enabled"
                  : "disabled",
              enrollmentValue1Checkbox: data.additionalSeatsTaken1.enabled
                ? "enabled"
                : "disabled",
              enrollmentValue1Name: data.additionalSeatsTaken1.label,
              enrollmentValue2Checkbox: data.additionalSeatsTaken2.enabled
                ? "enabled"
                : "disabled",
              enrollmentValue2Name: data.additionalSeatsTaken2.label,
            }}
            validate={validateWithZod(FormikSchema)}
            validateOnBlur={false}
            onSubmit={async (
              values: FormikType,
              formikHelpers: FormikHelpers<FormikType>
            ): Promise<void> => {
              try {
                if (!(await confirm())) {
                  return;
                }
                await updateCapacitiesConfigByEnrollmentPeriod({
                  path: {
                    enrollmentPeriodId,
                  },
                  body: toCapacityConfig(values),
                });
                await Promise.all([
                  queryClient.invalidateQueries({
                    queryKey: [FSQ.useGetCapacitiesConfigByEnrollmentPeriodKey],
                    exact: false,
                  }),
                  queryClient.invalidateQueries({
                    queryKey: [
                      FSQ.useGetCapacitiesBySchoolAndEnrollmentPeriodKey,
                    ],
                    exact: false,
                  }),
                  queryClient.invalidateQueries({
                    queryKey: [FSQ.useGetCapacitiesHistoryKey],
                    exact: false,
                  }),
                ]);
                avelaToast({
                  status: "success",
                  title: "Capacity settings updated",
                });
                formikHelpers.resetForm({ values });
              } catch (error) {
                console.error(error);
                avelaToast.genericError();
              }
            }}
          >
            {(formik) => (
              <>
                <NavigationBlocker hasChanges={formik.dirty} />
                <Flex
                  as={Form}
                  direction="column"
                  gap="10"
                  height="100%"
                  flexGrow={1}
                >
                  <Flex direction="column" gap="6">
                    <Heading as="h2" variant="admin">
                      Settings
                    </Heading>
                    <FormikSwitchableContent<"enabled" | "disabled">
                      shouldValidate={false}
                      label="Additional seats"
                      description={
                        <Text>
                          Seats will be calculated based on user entered values
                          and deduct from total capacity, lowering available
                          seats.{" "}
                          <LearnMore article="44143719550221-Capacities-Extra-Enrollment-Fields" />{" "}
                          about capacities.
                        </Text>
                      }
                      name="switch"
                      checked={["enabled", ""]}
                      unchecked={["disabled", ""]}
                      showContent={(value): boolean => value === "enabled"}
                      formLabelProps={{ fontSize: "md" }}
                    >
                      <Flex direction="column" gap="2">
                        <FormikSwitchableContent<"enabled" | "disabled">
                          variant="checkbox"
                          formLabelProps={{ margin: 0, fontSize: "md" }}
                          label={
                            <Flex gap="2" alignItems="center">
                              <Text>Enrollment value 1</Text>
                              <Tooltip
                                label="Column to deduct from total capacity"
                                hasArrow
                                placement="right"
                              >
                                <IconButton
                                  aria-label="help"
                                  icon={<RiInformationLine />}
                                  size="sm"
                                  colorScheme="gray"
                                  variant="ghost"
                                />
                              </Tooltip>
                            </Flex>
                          }
                          description=""
                          name="enrollmentValue1Checkbox"
                          checked={["enabled", ""]}
                          unchecked={["disabled", ""]}
                          showContent={(value): boolean => value === "enabled"}
                        >
                          <InputControl
                            label="Column name"
                            name="enrollmentValue1Name"
                            maxWidth="20rem"
                          />
                        </FormikSwitchableContent>
                        <FormikSwitchableContent<"enabled" | "disabled">
                          variant="checkbox"
                          formLabelProps={{ margin: 0, fontSize: "md" }}
                          label={
                            <Flex gap="2" alignItems="center">
                              <Text>Enrollment value 2</Text>
                              <Tooltip
                                label="Column to deduct from total capacity"
                                hasArrow
                                placement="right"
                              >
                                <IconButton
                                  aria-label="help"
                                  icon={<RiInformationLine />}
                                  size="sm"
                                  colorScheme="gray"
                                  variant="ghost"
                                />
                              </Tooltip>
                            </Flex>
                          }
                          description=""
                          name="enrollmentValue2Checkbox"
                          checked={["enabled", ""]}
                          unchecked={["disabled", ""]}
                          showContent={(value): boolean => value === "enabled"}
                        >
                          <InputControl
                            label="Column name"
                            name="enrollmentValue2Name"
                            maxWidth="20rem"
                          />
                        </FormikSwitchableContent>
                      </Flex>
                    </FormikSwitchableContent>
                  </Flex>

                  <Flex direction="column" gap="4">
                    <Flex alignItems="center">
                      <Heading as="h2" variant="admin">
                        Seats preview
                      </Heading>
                      <Spacer />
                      <Button
                        as={RouterLink}
                        variant="outline"
                        size="sm"
                        colorScheme="gray"
                        rightIcon={<RiExternalLinkLine />}
                        to={Url.OrgAdmin.CapacitiesConfig.index(organization)}
                        target="_blank"
                      >
                        Go to capacities
                      </Button>
                    </Flex>
                    <Capacities
                      hideEditButton
                      enrollmentPeriodId={enrollmentPeriodId}
                      hideAllSchoolsView
                      configOverride={toCapacityConfig(formik.values)}
                    />
                  </Flex>
                  <Spacer />
                  <AdminFormButtons insideTabPanel>
                    <Button
                      as={RouterLink}
                      to={Url.OrgAdmin.Enrollments.index(organization)}
                      variant="outline"
                      colorScheme="gray"
                    >
                      Back
                    </Button>
                    <Spacer />
                    <Button type="submit" isLoading={formik.isSubmitting}>
                      Update
                    </Button>
                  </AdminFormButtons>
                </Flex>
              </>
            )}
          </Formik>
        )}
      </RemoteDataView>
      {confirmationDialog}
    </>
  );
};

const FormikSchema = z
  .object({
    switch: z.union([z.literal("enabled"), z.literal("disabled")]),
    enrollmentValue1Checkbox: z.union([
      z.literal("enabled"),
      z.literal("disabled"),
    ]),
    enrollmentValue1Name: z.string(),
    enrollmentValue2Checkbox: z.union([
      z.literal("enabled"),
      z.literal("disabled"),
    ]),
    enrollmentValue2Name: z.string(),
  })
  .superRefine((values, ctx) => {
    if (values.switch === "disabled") {
      // no validation needed
      return;
    }

    if (
      values.enrollmentValue1Checkbox === "enabled" &&
      !isNonEmptyString(values.enrollmentValue1Name)
    ) {
      ctx.addIssue({
        message: "This field is required",
        path: ["enrollmentValue1Name"],
        code: "custom",
      });
    }

    if (
      values.enrollmentValue2Checkbox === "enabled" &&
      !isNonEmptyString(values.enrollmentValue2Name)
    ) {
      ctx.addIssue({
        message: "This field is required",
        path: ["enrollmentValue2Name"],
        code: "custom",
      });
    }

    if (
      values.enrollmentValue1Checkbox === "enabled" &&
      isNonEmptyString(values.enrollmentValue1Name) &&
      values.enrollmentValue2Checkbox === "enabled" &&
      isNonEmptyString(values.enrollmentValue2Name) &&
      values.enrollmentValue1Name.trim() === values.enrollmentValue2Name.trim()
    ) {
      ctx.addIssue({
        message: "Column name must be unique",
        path: ["enrollmentValue2Name"],
        code: "custom",
      });
    }
  });

type FormikType = z.infer<typeof FormikSchema>;

const Loading = () => {
  return (
    <Flex direction="column" gap="2">
      <Skeleton height="10" width="8rem" marginBottom="4" />
      <Skeleton height="10" width="15rem" />
      <Skeleton height="8" width="50rem" marginLeft="10" />
      <Skeleton height="20" width="25rem" marginLeft="10" />
      <Skeleton height="20" width="25rem" marginLeft="10" />
    </Flex>
  );
};

export function toCapacityConfig(values: FormikType): CapacityConfig {
  return {
    additionalSeatsTaken1: {
      enabled:
        values.switch === "enabled" &&
        values.enrollmentValue1Checkbox === "enabled",
      label: values.enrollmentValue1Name,
    },
    additionalSeatsTaken2: {
      enabled:
        values.switch === "enabled" &&
        values.enrollmentValue2Checkbox === "enabled",
      label: values.enrollmentValue2Name,
    },
  };
}
